import { useState } from 'react';
import { Avatar, Button, Dialog, InlineError, Textarea } from '../../components/ui';
import { formatCommentTime } from '../comments/CommentsSection';
import type { CommentWithAuthor } from '../comments/types';
import { drop3cErrorMessage } from './drop3c';
import { useForwardMessage, useForwardWouldWiden } from './drop3cHooks';
import { inertText } from './inertHtml';
import type { RoomListItem } from './rooms';

/** The first line of a message as plain text, for quotes and the pins list. */
export function firstLine(html: string): string {
  const text = inertText(html).trim();
  const line = text.split('\n')[0] ?? '';
  return line.length > 90 ? `${line.slice(0, 90)}…` : line;
}

/* ─── Pinned (N) strip ─── */

export interface PinnedItem {
  commentId: string;
  authorName: string;
  authorAvatarUrl: string | null;
  text: string;
  createdAt: string;
  /** The message's edited_at: set means it shows "(edited)" after the time. */
  editedAt: string | null;
  pinnedByName: string;
}

/**
 * Under the room header, closed by default (design-note "Pins"). Open, the
 * list scrolls inside a capped height (messages.css .messages-pins-list), so
 * 25 pins never push the room off the screen on desktop or phone (Shiroe's
 * ruling on t_3cb4c524). A Viewer gets Jump only.
 */
export function PinsStrip({
  items,
  canUnpin,
  onJump,
  onUnpin,
}: {
  items: PinnedItem[];
  canUnpin: boolean;
  onJump: (commentId: string) => void;
  onUnpin: (commentId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="messages-pins">
      <button
        type="button"
        className="messages-pins-bar"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        📌 Pinned ({items.length})
        <span className="messages-pins-toggle">{open ? '▾ hide' : '▸ show'}</span>
      </button>
      {open ? (
        <ul className="messages-pins-list" aria-label="Pinned messages">
          {items.map((item) => (
            <li key={item.commentId} className="messages-pins-item">
              <Avatar
                name={item.authorName === '' ? 'Someone' : item.authorName}
                imageUrl={item.authorAvatarUrl}
                size="xs"
              />
              <span className="messages-pins-text">
                <b>{item.authorName === '' ? 'Someone' : item.authorName}</b> {item.text}
              </span>
              <span className="messages-pins-meta">
                {formatCommentTime(item.createdAt)}
                {item.editedAt === null ? '' : ' (edited)'} · pinned by {item.pinnedByName}
              </span>
              <button
                type="button"
                className="messages-link-button"
                onClick={() => onJump(item.commentId)}
              >
                Jump
              </button>
              {canUnpin ? (
                <button
                  type="button"
                  className="messages-link-button"
                  onClick={() => onUnpin(item.commentId)}
                >
                  Unpin
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* ─── Quote of the message an action is about ─── */

function Quote({ comment, where }: { comment: CommentWithAuthor; where: string }) {
  const name = comment.author_name === '' ? 'Someone' : comment.author_name;
  return (
    <div className="messages-quote">
      <Avatar name={name} imageUrl={comment.author_avatar_url} size="xs" />
      <div className="messages-quote-text">
        <span className="messages-quote-meta">
          <b>{name}</b> in {where} · {formatCommentTime(comment.created_at)}
        </span>
        <span>{firstLine(comment.body)}</span>
      </div>
    </div>
  );
}

/* ─── Forward ─── */

/**
 * Forward (design-note "Forward" and "Forward warning", Kyu D5 option A:
 * warn, then allow). Before sending, forward_would_widen says whether the
 * target has anyone the source did not; if so the person sees the warning
 * with "Forward anyway" and "Cancel". If that check itself fails, nothing is
 * sent: sending without knowing would skip the warning.
 */
export function ForwardDialog({
  comment,
  sourceRoomId,
  sourceLabel,
  targets,
  titleOf,
  onClose,
  onForwarded,
}: {
  comment: CommentWithAuthor;
  sourceRoomId: string;
  sourceLabel: string;
  targets: { channels: RoomListItem[]; groups: RoomListItem[]; dms: RoomListItem[] };
  titleOf: (room: RoomListItem) => string;
  onClose: () => void;
  onForwarded: (targetTitle: string) => void;
}) {
  const widen = useForwardWouldWiden();
  const forward = useForwardMessage();
  const [targetId, setTargetId] = useState('');
  const [note, setNote] = useState('');
  const [step, setStep] = useState<'pick' | 'warn'>('pick');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const all = [...targets.channels, ...targets.groups, ...targets.dms];
  const target = all.find((room) => room.id === targetId) ?? null;
  const targetTitle = target === null ? '' : titleOf(target);

  async function send() {
    if (target === null) {
      setError('Pick where to send it.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await forward.mutateAsync({ sourceCommentId: comment.id, targetRoomId: target.id, note });
      onForwarded(targetTitle);
    } catch (cause) {
      setStep('pick');
      setError(
        drop3cErrorMessage(
          cause instanceof Error ? cause.message : '',
          'That message could not be forwarded. Try again.',
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  async function check() {
    if (target === null) {
      setError('Pick where to send it.');
      return;
    }
    setError('');
    setBusy(true);
    let widens: boolean;
    try {
      widens = await widen.mutateAsync({ sourceRoomId, targetRoomId: target.id });
    } catch {
      setBusy(false);
      setError('Could not check who will see it, so nothing was sent. Try again.');
      return;
    }
    setBusy(false);
    if (widens) {
      setStep('warn');
      return;
    }
    await send();
  }

  if (step === 'warn') {
    return (
      <Dialog open title={`Forward to ${targetTitle}?`} onClose={onClose}>
        <div className="messages-forward">
          <Quote comment={comment} where={sourceLabel} />
          <p className="messages-forward-warning" role="alert">
            This came from a private chat. Everyone in {targetTitle} will see it.
          </p>
          {error === '' ? null : <InlineError>{error}</InlineError>}
          <div className="messages-dialog-actions">
            <Button variant="secondary" onClick={() => setStep('pick')} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => void send()} disabled={busy}>
              Forward anyway
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  const groups: [string, RoomListItem[]][] = [
    ['Rooms', targets.channels],
    ['Group chats', targets.groups],
    ['Direct messages', targets.dms],
  ];

  return (
    <Dialog open title="Forward message" onClose={onClose}>
      <div className="messages-forward">
        <Quote comment={comment} where={sourceLabel} />
        <fieldset className="messages-forward-targets">
          <legend className="messages-forward-legend">Send to</legend>
          {all.length === 0 ? (
            <p className="messages-room-none">There is nowhere else to send it.</p>
          ) : null}
          {groups.map(([label, rooms]) =>
            rooms.length === 0 ? null : (
              <div key={label} className="messages-forward-group">
                <div className="messages-room-list-heading">{label}</div>
                {rooms.map((room) => (
                  <label key={room.id} className="messages-forward-option">
                    <input
                      type="radio"
                      name="forward-target"
                      value={room.id}
                      checked={targetId === room.id}
                      onChange={() => setTargetId(room.id)}
                    />
                    <span>{titleOf(room)}</span>
                  </label>
                ))}
              </div>
            ),
          )}
        </fieldset>
        <Textarea
          label="Add a note (optional)"
          rows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        {error === '' ? null : <InlineError>{error}</InlineError>}
        <div className="messages-dialog-actions">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void check()} disabled={busy || target === null}>
            {busy ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/* ─── Delete ─── */

/**
 * "Delete this message?" (design-note "Delete"). With replies the message
 * stays as a tombstone and the replies stay; without, it goes for everyone.
 */
export function DeleteMessageDialog({
  comment,
  where,
  replyCount,
  pinned,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  comment: CommentWithAuthor;
  where: string;
  replyCount: number;
  pinned: boolean;
  busy: boolean;
  error: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const what =
    replyCount > 0
      ? `The ${String(replyCount)} ${replyCount === 1 ? 'reply stays' : 'replies stay'} in the thread. The message shows as "This message was deleted".`
      : `It is removed for everyone in ${where}. This cannot be undone.`;
  return (
    <Dialog open title="Delete this message?" onClose={onCancel}>
      <div className="messages-forward">
        <Quote comment={comment} where={where} />
        <p className="messages-dialog-text">
          {what}
          {pinned ? ' Its pin is removed too.' : ''}
        </p>
        {error === '' ? null : <InlineError>{error}</InlineError>}
        <div className="messages-dialog-actions">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
